/**
 * Create Template Page
 * Full page for creating new templates
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  GripVertical, 
  FileText, 
  Wand2, 
  Upload,
  CheckCircle,
  Loader2,
  Save,
} from 'lucide-react';
import { useTemplates } from '@/hooks/useTemplates';
import { FileUploader } from '@/components/be-study/common/FileUploader';
import { AIProcessingLoader } from '@/components/be-study/common/AIProcessingLoader';
import { COUNTRIES } from '@/constants/beStudyMockData';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Section item for manual configuration
 */
function SectionItem({ section, index, onRemove, onUpdate }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg border bg-card" data-testid={`section-item-${index}`}>
      <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-grab shrink-0" />
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
            {index + 1}
          </span>
          <Input
            placeholder="Section title"
            value={section.title}
            onChange={(e) => onUpdate(index, 'title', e.target.value)}
            className="flex-1"
            data-testid={`section-title-${index}`}
          />
        </div>
        <Input
          placeholder="Description (optional)"
          value={section.description || ''}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          className="text-sm"
          data-testid={`section-description-${index}`}
        />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={section.required !== false}
              onChange={(e) => onUpdate(index, 'required', e.target.checked)}
              className="rounded"
            />
            Required section
          </label>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-red-500 shrink-0"
        onClick={() => onRemove(index)}
        data-testid={`remove-section-${index}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * AI Extracted section preview
 */
function ExtractedSectionPreview({ sections, onRemove }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Extracted Sections ({sections.length})</h4>
        <Badge variant="secondary" className="text-xs">AI Generated</Badge>
      </div>
      <ScrollArea className="h-[400px] rounded-lg border p-4">
        <div className="space-y-2">
          {sections.map((section, index) => (
            <div
              key={section.id || index}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{section.title}</p>
                {section.description && (
                  <p className="text-sm text-muted-foreground truncate">{section.description}</p>
                )}
              </div>
              {section.required && (
                <Badge variant="outline" className="text-xs shrink-0">Required</Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-red-500 shrink-0"
                onClick={() => onRemove(index)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

/**
 * Create Template Page Component
 */
export function CreateTemplatePage() {
  const navigate = useNavigate();
  const { createTemplate, extractSectionsFromDocument, isLoading } = useTemplates();

  const [activeTab, setActiveTab] = useState('manual');
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    description: '',
  });
  const [sections, setSections] = useState([]);
  const [extractedSections, setExtractedSections] = useState([]);
  const [errors, setErrors] = useState({});
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAddSection = () => {
    setSections((prev) => [
      ...prev,
      { id: `SEC-${Date.now()}`, title: '', description: '', required: true },
    ]);
  };

  const handleUpdateSection = (index, field, value) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const handleRemoveSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExtractedSection = (index) => {
    setExtractedSections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (file) => {
    setIsExtracting(true);
    setExtractionProgress(0);

    // Simulate progress
    const interval = setInterval(() => {
      setExtractionProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    const result = await extractSectionsFromDocument(file);

    clearInterval(interval);
    setExtractionProgress(100);

    if (result?.sections) {
      setExtractedSections(result.sections);
    }

    setTimeout(() => {
      setIsExtracting(false);
      setExtractionProgress(0);
    }, 500);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.country) {
      newErrors.country = 'Please select a country';
    }

    const activeSections = activeTab === 'manual' ? sections : extractedSections;
    if (activeSections.length === 0) {
      newErrors.sections = 'At least one section is required';
    } else if (activeTab === 'manual') {
      const hasEmptyTitles = sections.some((s) => !s.title.trim());
      if (hasEmptyTitles) {
        newErrors.sections = 'All sections must have a title';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSaving(true);

    const activeSections = activeTab === 'manual' ? sections : extractedSections;

    const result = createTemplate({
      name: formData.name.trim(),
      country: formData.country,
      description: formData.description.trim(),
      sections: activeSections.map((s, i) => ({
        ...s,
        order: i + 1,
        required: s.required !== false,
      })),
      createdVia: activeTab === 'manual' ? 'manual' : 'ai_extraction',
    });

    setIsSaving(false);

    if (result.success) {
      toast.success('Template created successfully');
      navigate(ROUTES.TEMPLATES);
    } else {
      toast.error(result.error || 'Failed to create template');
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.TEMPLATES);
  };

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="create-template-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create New Template</h1>
            <p className="text-muted-foreground">
              Define the structure for bioequivalence study reports
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left Column - Basic Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Template Details</CardTitle>
                  <CardDescription>
                    Basic information about the template
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Name */}
                  <div className="space-y-2">
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      placeholder="e.g., FDA BE Study Report"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                      data-testid="template-name-input"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500">{errors.name}</p>
                    )}
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label>Country/Region *</Label>
                    <Select
                      value={formData.country}
                      onValueChange={(value) => handleChange('country', value)}
                    >
                      <SelectTrigger
                        className={errors.country ? 'border-red-500' : ''}
                        data-testid="template-country-select"
                      >
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.country && (
                      <p className="text-xs text-red-500">{errors.country}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="template-description">Description</Label>
                    <Textarea
                      id="template-description"
                      placeholder="Brief description of this template..."
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      data-testid="template-description-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sections</span>
                      <span className="font-medium">
                        {activeTab === 'manual' ? sections.length : extractedSections.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Required</span>
                      <span className="font-medium">
                        {activeTab === 'manual'
                          ? sections.filter((s) => s.required !== false).length
                          : extractedSections.filter((s) => s.required !== false).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Method</span>
                      <Badge variant="outline" className="text-xs">
                        {activeTab === 'manual' ? 'Manual' : 'AI Extraction'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Sections */}
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>Define Sections</CardTitle>
                  <CardDescription>
                    Choose how you want to create the template sections
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="manual" className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Manual Configuration
                      </TabsTrigger>
                      <TabsTrigger value="upload" className="flex items-center gap-2">
                        <Wand2 className="h-4 w-4" />
                        AI Extraction
                      </TabsTrigger>
                    </TabsList>

                    {/* Manual Configuration Tab */}
                    <TabsContent value="manual" className="space-y-4">
                      {sections.length > 0 ? (
                        <ScrollArea className="h-[450px] pr-4">
                          <div className="space-y-3">
                            {sections.map((section, index) => (
                              <SectionItem
                                key={section.id}
                                section={section}
                                index={index}
                                onRemove={handleRemoveSection}
                                onUpdate={handleUpdateSection}
                              />
                            ))}
                          </div>
                        </ScrollArea>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg">
                          <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground mb-2">No sections added yet</p>
                          <p className="text-sm text-muted-foreground mb-4">
                            Start by adding sections to your template
                          </p>
                        </div>
                      )}

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleAddSection}
                        data-testid="add-section-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Section
                      </Button>

                      {errors.sections && (
                        <p className="text-sm text-red-500 text-center">{errors.sections}</p>
                      )}
                    </TabsContent>

                    {/* AI Extraction Tab */}
                    <TabsContent value="upload" className="space-y-4">
                      {isExtracting ? (
                        <div className="py-8">
                          <AIProcessingLoader
                            variant="card"
                            title="Extracting Sections"
                            description="AI is analyzing your document structure"
                            progress={extractionProgress}
                            step="extracting"
                          />
                        </div>
                      ) : extractedSections.length > 0 ? (
                        <>
                          <ExtractedSectionPreview
                            sections={extractedSections}
                            onRemove={handleRemoveExtractedSection}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              setExtractedSections([]);
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Different Document
                          </Button>
                        </>
                      ) : (
                        <FileUploader
                          onUpload={handleFileUpload}
                          maxFiles={1}
                          accept={['.pdf', '.docx', '.doc']}
                          dropzoneText="Upload a template document for AI extraction"
                          supportedFormatsText="Supported: PDF, Word (.docx, .doc)"
                        />
                      )}

                      {errors.sections && activeTab === 'upload' && (
                        <p className="text-sm text-red-500 text-center">{errors.sections}</p>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || isExtracting}
              data-testid="create-template-submit-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Create Template
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </BaseLayout>
  );
}

export default CreateTemplatePage;
